import AdminPageHero from "../components/admin/AdminPageHero";
import AdminPostList from "../components/admin/AdminPostList";
import useAdminOperation from "../hooks/useAdminOperation";
import "./AdminSecondary.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarDays, Check, ChevronLeft, ChevronRight, Image as ImageIcon, RefreshCw, Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";

const localStartValue = () => {
  const date = new Date(Date.now() + 15 * 60 * 1000);
  date.setMinutes(Math.ceil(date.getMinutes() / 5) * 5, 0, 0);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function defaultCaption(item, platform) {
  if (platform === "instagram") {
    return `${item.title}\n\nHandmade wood decor by Beyond Board Woodworking. Find this piece in our gallery, or ask us to make something personal to you.\n\n#BeyondBoardWoodwork #HandmadeDecor #CustomWoodworking`;
  }
  return `New from Beyond Board Woodworking: ${item.title}\n\n${item.description || "Handmade wood decor, made with care."}\n\nSee this piece in our gallery and ask us about a custom design.`;
}

function formatSchedule(value) {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function localDateTimeValue(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

const STATUS_STYLES = {
  SCHEDULED: "border-sky-400/30 bg-sky-400/10 text-sky-200",
  QUEUED: "border-amber-400/30 bg-amber-400/10 text-amber-200",
  PUBLISHING: "border-amber-400/30 bg-amber-400/10 text-amber-200",
  PUBLISHED: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
  FAILED: "border-rose-400/30 bg-rose-400/10 text-rose-200",
  CANCELLED: "border-stone-400/20 bg-stone-400/5 text-stone-400",
};

export default function CampaignManager() {
  const beginOperation = useAdminOperation();
  const [library, setLibrary] = useState([]);
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewId, setPreviewId] = useState("");
  const [editPlatform, setEditPlatform] = useState("facebook");
  const [captions, setCaptions] = useState({});
  const [campaignName, setCampaignName] = useState("Seasonal Product Campaign");
  const [platforms, setPlatforms] = useState([]);
  const [startAt, setStartAt] = useState(localStartValue);
  const [spacingHours, setSpacingHours] = useState("24");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [calendarDay, setCalendarDay] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [schedulingDraftId, setSchedulingDraftId] = useState("");
  const [previewDraftId, setPreviewDraftId] = useState("");
  const [draftStartAt, setDraftStartAt] = useState(localStartValue);
  const [draftSpacingHours, setDraftSpacingHours] = useState("24");

  const load = useCallback(async () => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    setLoading(true);
    setLoadError("");
    try {
      const [libraryRes, postsRes, accountsRes] = await Promise.all([
        api.get("/admin/social/library"),
        api.get("/admin/social/posts"),
        api.get("/admin/social/accounts"),
      ]);
      if (!operationCurrent()) return;
      const nextLibrary = Array.isArray(libraryRes.data) ? libraryRes.data : [];
      setLibrary(nextLibrary);
      setSelectedIds((current) => current.filter((id) => nextLibrary.some((item) => item.id === id && item.advertisable)));
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
      const nextAccounts = Array.isArray(accountsRes.data) ? accountsRes.data : [];
      setAccounts(nextAccounts);
      setPlatforms((current) => current.filter((platform) => nextAccounts.some((account) => account.platform === platform && account.connectionStatus === "connected")));
      setPreviewId((current) => current && nextLibrary.some((item) => item.id === current) ? current : nextLibrary[0]?.id || "");
    } catch (error) {
      if (!operationCurrent()) return;
      setLoadError(formatApiError(error));
      toast.error(formatApiError(error));
    } finally {
      if (operationCurrent()) {
      setLoading(false);
      }
    }
  }, [beginOperation]);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => ["All", ...new Set(library.flatMap((item) => item.categories || []))], [library]);
  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return library.filter((item) => {
      const inCategory = category === "All" || (item.categories || []).includes(category);
      const matches = !query || [item.title, item.description, ...(item.categories || [])].join(" ").toLowerCase().includes(query);
      return inCategory && matches;
    });
  }, [library, search, category]);
  const selectedItem = library.find((item) => item.id === previewId) || null;
  const connected = (platform) => accounts.some((account) => account.platform === platform && account.connectionStatus === "connected");
  const availableVisibleItems = visibleItems.filter((item) => item.advertisable);
  const allVisibleSelected = availableVisibleItems.length > 0 && availableVisibleItems.every((item) => selectedIds.includes(item.id));
  const calendarDays = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      const key = dateKey(date);
      const dayPosts = posts.filter((post) => {
        const value = post.scheduledFor || post.publishedAt || (["QUEUED", "PUBLISHING"].includes(post.status) ? post.createdAt : null);
        return value && dateKey(new Date(value)) === key;
      });
      return { date, key, dayPosts, currentMonth: date.getMonth() === calendarMonth.getMonth() };
    });
  }, [calendarMonth, posts]);
  const visiblePosts = calendarDay ? posts.filter((post) => {
    const value = post.scheduledFor || post.publishedAt || (["QUEUED", "PUBLISHING"].includes(post.status) ? post.createdAt : null);
    return value && dateKey(new Date(value)) === calendarDay;
  }) : posts;
  const draftCampaigns = useMemo(() => {
    const grouped = new Map();
    posts.filter((post) => post.status === "DRAFT" && post.campaignId).forEach((post) => {
      const group = grouped.get(post.campaignId) || { id: post.campaignId, name: post.campaignName || "Imported campaign", drafts: 0 };
      group.drafts += 1;
      grouped.set(post.campaignId, group);
    });
    return [...grouped.values()];
  }, [posts]);

  const togglePlatform = (platform) => {
    setPlatforms((current) => current.includes(platform) ? current.filter((item) => item !== platform) : [...current, platform]);
  };
  const toggleItem = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    setPreviewId(id);
  };
  const toggleAllVisible = () => {
    setSelectedIds((current) => allVisibleSelected
      ? current.filter((id) => !availableVisibleItems.some((item) => item.id === id))
      : [...new Set([...current, ...availableVisibleItems.map((item) => item.id)])]);
  };
  const selectCalendarDay = (day) => {
    const time = startAt?.slice(11, 16) || "09:00";
    const [hours, minutes] = time.split(":").map(Number);
    const next = new Date(day.date);
    next.setHours(hours || 0, minutes || 0, 0, 0);
    setCalendarMonth(new Date(day.date.getFullYear(), day.date.getMonth(), 1));
    setCalendarDay(day.key);
    setStartAt(localDateTimeValue(next));
  };
  const captionValue = selectedItem
    ? captions[selectedItem.id]?.[editPlatform] ?? defaultCaption(selectedItem, editPlatform)
    : "";
  const updateCaption = (value) => {
    if (!selectedItem) return;
    setCaptions((current) => ({ ...current, [selectedItem.id]: { ...(current[selectedItem.id] || {}), [editPlatform]: value } }));
  };

  const scheduleCampaign = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    event.preventDefault();
    if (!selectedIds.length) return toast.error("Choose at least one available product.");
    if (!platforms.length) return toast.error("Choose a connected social account.");
    setSaving(true);
    try {
      const response = await api.post("/admin/social/campaigns", {
        campaignName,
        galleryIds: selectedIds,
        platforms,
        startAt: new Date(startAt).toISOString(),
        intervalMinutes: Math.round(Number(spacingHours || 0) * 60),
        businessTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
        captions,
      });
      if (!operationCurrent()) return;
      const created = response.data?.posts || [];
      const skipped = response.data?.skipped || [];
      if (!created.length) {
        toast.error(skipped[0]?.reason || "No products could be scheduled.");
      } else {
        toast.success(`${created.length} post${created.length === 1 ? "" : "s"} scheduled${skipped.length ? `; ${skipped.length} unavailable item${skipped.length === 1 ? " was" : "s were"} skipped` : ""}.`);
        setSelectedIds([]);
        await load();
      if (!operationCurrent()) return;
      }
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(error));
    } finally {
      if (operationCurrent()) {
      setSaving(false);
      }
    }
  };

  const cancelPost = async (id) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    try {
      await api.post(`/admin/social/posts/${id}/cancel`);
      if (!operationCurrent()) return;
      toast.success("Scheduled post cancelled.");
      await load();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return; toast.error(formatApiError(error)); }
  };
  const retryPost = async (id) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    try {
      await api.post(`/admin/social/posts/${id}/retry`);
      if (!operationCurrent()) return;
      toast.success("Post queued for retry.");
      await load();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return; toast.error(formatApiError(error)); }
  };

  const importDraftFile = async (event) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      if (file.name.toLowerCase().endsWith(".zip")) {
        const form = new FormData();
        form.append("file", file);
        const response = await api.post("/admin/social/campaign-packages/import", form);
      if (!operationCurrent()) return;
        const result = response.data || {};
        toast.success(`${result.imported || 0} campaign drafts imported with ${result.assetsStored || 0} original images. Drafts remain unpublished.`);
        if (result.skipped?.length) toast.error(`${result.skipped.length} package item${result.skipped.length === 1 ? " was" : "s were"} left out. ${result.collectionsAndReels || result.skipped[0].reason || "Review the import details."}`);
        await load();
      if (!operationCurrent()) return;
        return;
      }
      const campaign = JSON.parse(await file.text());
      if (!operationCurrent()) return;
      if (!Array.isArray(campaign.drafts)) throw new Error("Choose the prepared campaign JSON file, which contains a drafts list.");
      const response = await api.post("/admin/social/drafts/import", {
        campaignName: campaign.campaign_name || file.name.replace(/\.json$/i, ""),
        drafts: campaign.drafts,
      });
      if (!operationCurrent()) return;
      const result = response.data || {};
      toast.success(`${result.imported || 0} drafts imported${result.updated ? `; ${result.updated} existing drafts refreshed` : ""}. They remain unpublished.`);
      if (result.skipped?.length) toast.error(`${result.skipped.length} draft${result.skipped.length === 1 ? " was" : "s were"} skipped. First: ${result.skipped[0].reason}`);
      await load();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(formatApiError(error));
    } finally {
      if (operationCurrent()) {
      setImporting(false);
      event.target.value = "";
      }
    }
  };

  const scheduleDraftCampaign = async (campaign) => {
    const operationCurrent = beginOperation();
    if (!operationCurrent()) return;
    if (schedulingDraftId) return;
    if (!draftStartAt) return toast.error("Choose the first post date and time.");
    setSchedulingDraftId(campaign.id);
    try {
      const response = await api.post(`/admin/social/campaigns/${encodeURIComponent(campaign.id)}/schedule-drafts`, {
        startAt: new Date(draftStartAt).toISOString(),
        intervalMinutes: Math.round(Number(draftSpacingHours || 0) * 60),
        businessTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
      });
      if (!operationCurrent()) return;
      const scheduled = response.data?.scheduled || [];
      const skipped = response.data?.skipped || [];
      if (scheduled.length) toast.success(`${scheduled.length} draft post${scheduled.length === 1 ? "" : "s"} scheduled. Nothing publishes before its scheduled time.`);
      else toast.error(skipped[0]?.reason || "No drafts could be scheduled.");
      await load();
      if (!operationCurrent()) return;
    } catch (error) {
      if (!operationCurrent()) return;
      toast.error(`${formatApiError(error)} Refresh the queue before trying again.`);
      await load();
      if (!operationCurrent()) return;
    } finally {
      if (operationCurrent()) setSchedulingDraftId(""); }
  };

  return (
    <main className="bbw-admin-secondary min-h-screen bg-[#15191C] text-[#F5F0E6]" data-testid="campaign-manager-page">
      <AdminPageHero title="Campaigns" description="Plan, review and publish. Keep upcoming work separate from your publishing history." metrics={[
        { label: "drafts", value: loading || loadError ? "—" : posts.filter(post => post.status === "DRAFT").length },
        { label: "scheduled", value: loading || loadError ? "—" : posts.filter(post => ["SCHEDULED", "QUEUED"].includes(post.status)).length },
        { label: "need attention", value: loading || loadError ? "—" : posts.filter(post => post.status === "FAILED").length, tone: "attention" },
      ]}>
        <Button onClick={() => document.getElementById("campaign-compose")?.scrollIntoView({ behavior: "auto", block: "start" })}>Create campaign</Button>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </AdminPageHero>

      {loadError && <p className="admin-campaign-load-error" role="alert">Campaign data could not be refreshed: {loadError}. Counts and connections are unverified. Refresh before scheduling.</p>}
      <div className="admin-campaign-workspace">      <section className="admin-campaign-queue">
        <div className="admin-secondary-action-row mb-4 flex items-end justify-between gap-3"><div><h2 className="font-display text-2xl">Publishing queue</h2><p className="mt-1 text-sm text-stone-400">{calendarDay ? `Posts for ${new Date(`${calendarDay}T12:00:00`).toLocaleDateString()}` : "Review drafts, upcoming posts and failures. Past activity stays in history."}</p></div><div className="flex items-center gap-3">{calendarDay && <Button size="sm" variant="outline" onClick={() => setCalendarDay("")} className="border-stone-100/15 bg-transparent text-stone-200">Show all</Button>}<span className="text-xs text-stone-500">Backend checks for due posts every 30 seconds.</span></div></div>
        <div className="space-y-2">
          <AdminPostList key={calendarDay || "all-days"} posts={visiblePosts}>{(post) => (
            <article key={post.id} className="admin-campaign-post rounded-xl border border-stone-100/10 bg-zinc-900/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">{post.mediaUrl && <img src={post.mediaUrl} alt="" className="h-12 w-12 rounded-md object-cover" />}<div className="min-w-0"><p className="truncate font-medium">{post.campaignName || post.itemName || (post.sourceType === "EVENT" ? "Event post" : "Gallery post")}</p><p className="mt-1 text-xs text-stone-500">{post.platform} · {formatSchedule(post.scheduledFor || post.publishedAt || post.createdAt)}</p>{post.failureMessage && <p className="mt-1 text-xs text-rose-300">{post.failureMessage}</p>}</div></div>
                <div className="flex items-center gap-3"><span className={`rounded-full border px-2.5 py-1 text-xs ${STATUS_STYLES[post.status] || STATUS_STYLES.CANCELLED}`}>{post.status}</span>{post.status === "DRAFT" && <Button size="sm" variant="outline" onClick={() => setPreviewDraftId((current) => current === post.id ? "" : post.id)} className="border-stone-100/15 bg-transparent text-stone-200">{previewDraftId === post.id ? "Hide preview" : "Preview ad"}</Button>}{post.status === "FAILED" && <Button size="sm" variant="outline" onClick={() => retryPost(post.id)} className="border-stone-100/15 bg-transparent text-stone-200">Retry</Button>}{["SCHEDULED", "QUEUED"].includes(post.status) && <Button size="sm" variant="outline" onClick={() => cancelPost(post.id)} className="border-rose-400/25 bg-transparent text-rose-200"><X className="mr-1 h-3.5 w-3.5" />Cancel</Button>}</div>
              </div>
              {previewDraftId === post.id && <div className="mt-4 grid gap-4 border-t border-stone-100/10 pt-4 sm:grid-cols-[minmax(0,1fr)_220px]">
                <p className="whitespace-pre-wrap text-sm text-stone-300">{post.caption}</p>
                <div className="space-y-2">{post.mediaUrl && <div><p className="mb-1 text-xs text-stone-500">Feed creative</p><img src={post.mediaUrl} alt={`${post.itemName || "Campaign"} feed creative`} className="max-h-52 w-full rounded-lg object-contain object-left" /></div>}{post.storyMediaUrl && <div><p className="mb-1 text-xs text-stone-500">Story creative · saved for review</p><img src={post.storyMediaUrl} alt={`${post.itemName || "Campaign"} Story creative`} className="max-h-52 w-full rounded-lg object-contain object-left" /></div>}</div>
              </div>}
            </article>
          )}</AdminPostList>
        </div>
      </section>
<aside className="admin-campaign-support">      <section className="admin-campaign-side">
        <div className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#D8A34B]" /><div><h2 className="font-display text-2xl">Posting calendar</h2><p className="mt-1 text-sm text-stone-400">Choose a day to set the first post time and filter its queue.</p></div></div>
            <div className="flex items-center gap-2"><Button type="button" size="sm" variant="outline" aria-label="Previous month" onClick={() => setCalendarMonth((value) => new Date(value.getFullYear(), value.getMonth() - 1, 1))} className="border-stone-100/15 bg-transparent text-stone-200"><ChevronLeft className="h-4 w-4" /></Button><span className="min-w-32 text-center text-sm font-medium">{calendarMonth.toLocaleDateString([], { month: "long", year: "numeric" })}</span><Button type="button" size="sm" variant="outline" aria-label="Next month" onClick={() => setCalendarMonth((value) => new Date(value.getFullYear(), value.getMonth() + 1, 1))} className="border-stone-100/15 bg-transparent text-stone-200"><ChevronRight className="h-4 w-4" /></Button></div>
          </div>
          <div className="admin-campaign-weekdays grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.16em] text-stone-500">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <span key={day} className="py-2">{day}</span>)}</div>
          <div className="admin-campaign-calendar grid grid-cols-7 gap-1">
            {calendarDays.map((day) => (
              <button key={day.key} type="button" onClick={() => selectCalendarDay(day)} aria-pressed={calendarDay === day.key} aria-label={`${day.date.toLocaleDateString()}${day.dayPosts.length ? `, ${day.dayPosts.length} posts` : ""}`} className={`min-h-14 rounded-lg border p-2 text-left transition ${calendarDay === day.key ? "border-[#D8A34B]/60 bg-[#D8A34B]/10" : "border-stone-100/5 bg-zinc-950/40 hover:border-stone-100/20"} ${day.currentMonth ? "text-stone-200" : "text-stone-600"}`}>
                <span className="text-xs">{day.date.getDate()}</span>
                {day.dayPosts.length > 0 && <span className="mt-1 block truncate text-[10px] text-[#8FE8F8]">{day.dayPosts.length} post{day.dayPosts.length === 1 ? "" : "s"}</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

<section className="admin-campaign-accounts"><h2>Social accounts</h2><p>Check connection and publishing settings before scheduling.</p>{["facebook", "instagram"].map(platform => <div key={platform}><strong>{platform === "facebook" ? "Facebook" : "Instagram"}</strong><span className={connected(platform) ? "is-ready" : "is-attention"}>{loading || loadError ? "Unverified" : connected(platform) ? "Connected" : "Needs setup"}</span></div>)}<Link to="/admin/settings/social-accounts">Review setup →</Link></section></aside></div>
      <div id="campaign-compose" className="admin-campaign-compose mx-auto grid max-w-7xl gap-6 px-6 pb-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
        <section className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="font-display text-2xl">Content library</h2><p className="mt-1 text-sm text-stone-400">{library.length} gallery items · {selectedIds.length} selected</p></div>
            <Button type="button" variant="outline" onClick={toggleAllVisible} className="border-stone-100/15 bg-transparent text-stone-200">
              {allVisibleSelected ? "Clear visible" : "Select available"}
            </Button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px]">
            <label className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-stone-500" /><Input aria-label="Search products" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search products or categories" className="border-stone-100/10 bg-zinc-950 pl-9 text-stone-100" /></label>
            <select aria-label="Filter category" value={category} onChange={(event) => setCategory(event.target.value)} className="rounded-md border border-stone-100/10 bg-zinc-950 px-3 py-2 text-sm text-stone-100">
              {categories.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
          <div className="mt-4 max-h-[580px] space-y-2 overflow-y-auto pr-1" data-testid="campaign-library">
            {loading ? <p className="py-8 text-center text-sm text-stone-400">Loading products…</p> : visibleItems.map((item) => {
              const checked = selectedIds.includes(item.id);
              return (
                <div key={item.id} className={`flex items-center gap-3 rounded-xl border p-3 ${previewId === item.id ? "border-[#D8A34B]/45 bg-[#D8A34B]/5" : "border-stone-100/10 bg-zinc-950/50"}`}>
                  <input aria-label={`Select ${item.title}`} type="checkbox" checked={checked} disabled={!item.advertisable} onChange={() => toggleItem(item.id)} className="h-4 w-4 accent-[#D8A34B]" />
                  <button type="button" onClick={() => setPreviewId(item.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    {item.image_url ? <img src={item.image_url} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <span className="grid h-14 w-14 place-items-center rounded-lg bg-stone-100/5"><ImageIcon className="h-5 w-5 text-stone-500" /></span>}
                    <span className="min-w-0"><span className="block truncate font-medium text-stone-100">{item.title}</span><span className="mt-1 block truncate text-xs text-stone-500">{(item.categories || []).join(" · ") || "Uncategorized"}</span><span className={`mt-1 block text-xs ${item.advertisable ? "text-emerald-300" : "text-amber-300"}`}>{item.advertisable ? (item.available == null ? "Available" : `${item.available} available`) : item.availabilityIssue || "Photo required"}</span></span>
                  </button>
                  {checked && <Check className="h-4 w-4 text-emerald-300" />}
                </div>
              );
            })}
            {!loading && !visibleItems.length && <p className="py-10 text-center text-sm text-stone-500">No products match this filter.</p>}
          </div>
        </section>

        <div className="space-y-6">
          <section className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
            <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#D8A34B]" /><h2 className="font-display text-2xl">Post preview</h2></div>
            {selectedItem ? (
              <>
                <div className="mt-4 overflow-hidden rounded-xl border border-stone-100/10 bg-zinc-950">
                  {selectedItem.image_url ? <img src={selectedItem.image_url} alt={selectedItem.title} className="max-h-64 w-full object-cover" /> : <div className="grid h-40 place-items-center text-stone-500">Photo needed</div>}
                  <div className="p-4"><h3 className="font-semibold">{selectedItem.title}</h3><p className="mt-1 text-xs text-stone-500">{(selectedItem.categories || []).join(" · ")}</p><a href={`/gallery?item=${encodeURIComponent(selectedItem.id)}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[#8FE8F8] underline">Open matching item link</a></div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3"><label htmlFor="caption-platform" className="text-xs uppercase tracking-[0.18em] text-stone-500">Caption</label><select id="caption-platform" value={editPlatform} onChange={(event) => setEditPlatform(event.target.value)} className="rounded-md border border-stone-100/10 bg-zinc-950 px-3 py-2 text-sm text-stone-100"><option value="facebook">Facebook</option><option value="instagram">Instagram</option></select></div>
                <Textarea aria-label={`${editPlatform} caption`} value={captionValue} onChange={(event) => updateCaption(event.target.value)} maxLength={2000} className="mt-2 min-h-36 border-stone-100/10 bg-zinc-950 text-stone-100" />
                <p className="mt-1 text-right text-xs text-stone-500">{captionValue.length}/2,000</p>
              </>
            ) : <p className="admin-campaign-preview-empty mt-4 rounded-xl p-6 text-sm">Choose a product to preview its photo and caption.</p>}
          </section>

          <form onSubmit={scheduleCampaign} className="admin-campaign-schedule rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
            <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#D8A34B]" /><h2 className="font-display text-2xl">Schedule a batch</h2></div>
            <label className="mt-4 block text-sm text-stone-300">Campaign name<Input required maxLength={120} value={campaignName} onChange={(event) => setCampaignName(event.target.value)} className="mt-1 border-stone-100/10 bg-zinc-950 text-stone-100" /></label>
            <fieldset className="mt-4"><legend className="mb-2 text-sm text-stone-300">Post to</legend><div className="flex flex-wrap gap-2">{["facebook", "instagram"].map((platform) => <label key={platform} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm capitalize ${connected(platform) ? "border-stone-100/15 text-stone-200" : "border-stone-100/5 text-stone-500"}`}><input type="checkbox" disabled={!connected(platform)} checked={platforms.includes(platform)} onChange={() => togglePlatform(platform)} className="accent-[#D8A34B]" />{platform}{!connected(platform) && " · connect first"}</label>)}</div></fieldset>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm text-stone-300">First post<Input aria-label="First post date and time" type="datetime-local" required value={startAt} onChange={(event) => setStartAt(event.target.value)} className="mt-1 border-stone-100/10 bg-zinc-950 text-stone-100" /></label>
              <label className="block text-sm text-stone-300">Spacing between products (hours)<Input aria-label="Spacing between products in hours" type="number" min="0" max="168" step="1" value={spacingHours} onChange={(event) => setSpacingHours(event.target.value)} className="mt-1 border-stone-100/10 bg-zinc-950 text-stone-100" /></label>
            </div>
            <p className="mt-3 text-xs text-stone-500">Posts go out in the selected order, with Facebook and Instagram sharing each product’s time slot. Scheduling stays separate from Auto Post.</p>
            <Button type="submit" disabled={loading || Boolean(loadError) || saving || !selectedIds.length || !platforms.length} className="mt-4 w-full bg-[#26C6D5] text-[#15191C] hover:bg-[#1ab7d2]"><Send className="mr-2 h-4 w-4" />{saving ? "Scheduling…" : `Schedule ${selectedIds.length} product${selectedIds.length === 1 ? "" : "s"}`}</Button>
          </form>
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-6 pb-8 lg:px-10">
        <div className="rounded-2xl border border-stone-100/10 bg-zinc-900/70 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div><h2 className="font-display text-2xl">Prepared ad drafts</h2><p className="mt-1 max-w-2xl text-sm text-stone-400">Import the verified campaign ZIP to bring in its original feed and Story artwork, or a prepared JSON draft list. Everything stays unpublished until you schedule it.</p></div>
            <label className={`admin-campaign-import inline-flex cursor-pointer items-center rounded-md bg-[#26C6D5] px-4 py-2 text-sm font-medium text-[#15191C] hover:bg-[#1ab7d2] ${importing ? "pointer-events-none opacity-60" : ""}`}>
              {importing ? "Importing…" : "Import campaign ZIP or JSON"}
              <input aria-label="Import campaign ZIP or JSON" data-testid="campaign-draft-import" type="file" accept="application/zip,.zip,application/json,.json" onChange={importDraftFile} disabled={importing} className="sr-only" />
            </label>
          </div>
          {draftCampaigns.length > 0 && <div className="mt-5 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm text-stone-300">First draft post<Input aria-label="First draft post date and time" type="datetime-local" value={draftStartAt} onChange={(event) => setDraftStartAt(event.target.value)} className="mt-1 border-stone-100/10 bg-zinc-950 text-stone-100" /></label>
              <label className="block text-sm text-stone-300">Spacing between products (hours)<Input aria-label="Draft spacing in hours" type="number" min="0" max="168" step="1" value={draftSpacingHours} onChange={(event) => setDraftSpacingHours(event.target.value)} className="mt-1 border-stone-100/10 bg-zinc-950 text-stone-100" /></label>
            </div>
            {draftCampaigns.map((campaign) => <div key={campaign.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-100/10 bg-zinc-950/60 p-4">
              <p className="min-w-0 truncate text-sm text-stone-200">{campaign.name}<span className="ml-2 text-stone-500">{campaign.drafts} unpublished drafts</span></p>
              <Button type="button" size="sm" disabled={Boolean(schedulingDraftId)} onClick={() => scheduleDraftCampaign(campaign)} className="bg-[#D8A34B] text-[#15191C] hover:bg-[#e2b661]">{schedulingDraftId === campaign.id ? "Scheduling…" : "Schedule all drafts"}</Button>
            </div>)}
          </div>}
        </div>
      </section>

    </main>
  );
}

